/**
 * Keeps the Connector's per-renderer gapless flag in step with the user's saved choice (#11529).
 * The Connector gates SetNext on its own flag, so a stale "off" there silences gapless no matter
 * what the Devices card shows. Intent = the user's saved pref, else the default (on).
 */

import { diagnosticLog } from '../utils/diagnosticLogger';
import { getRendererGaplessEnabled } from './gaplessPlaybackSettings';

const RETRY_WINDOW_MS = 15_000;
const lastAttemptAt = new Map<string, number>();

export class GaplessAclDeniedError extends Error {}

export async function pushGaplessToConnector(rendererId: string, enabled: boolean): Promise<void> {
  const { isConnectorAvailable, setRendererGaplessEnabled } = await import('../lib/connector');
  if (!isConnectorAvailable()) return;
  try {
    await setRendererGaplessEnabled(rendererId, enabled);
  } catch (err) {
    const { isLocalLibraryCommandError } = await import('./localLibraryIpcGate');
    if (isLocalLibraryCommandError(err)) {
      throw new GaplessAclDeniedError(
        'Gapless toggle is not allowed on this Connector build — update Maestro or check capabilities/default.json',
      );
    }
    throw err;
  }
}

export function reportGaplessConnectorFailure(rendererId: string, enabled: boolean, err: unknown): void {
  diagnosticLog('warn', 'playback', 'gapless_connector_push_failed', {
    renderer_id: rendererId,
    enabled,
    error: err instanceof Error ? err.message : String(err),
  });
}

type ReconcileZone = { id: string; metadata?: Record<string, unknown> };

/** Call with the raw zones from the Connector (before the pref overlay hides its reported flag). */
export function reconcileConnectorGapless<T extends ReconcileZone>(zones: T[]): T[] {
  const now = Date.now();
  for (const z of zones) {
    const reported = z.metadata?.gapless_enabled;
    if (z.metadata?.source !== 'native' || typeof reported !== 'boolean') continue;
    const intended = getRendererGaplessEnabled(z.id);
    if (reported === intended) {
      lastAttemptAt.delete(z.id);
      continue;
    }
    if (now - (lastAttemptAt.get(z.id) ?? -Infinity) < RETRY_WINDOW_MS) continue;
    lastAttemptAt.set(z.id, now);
    pushGaplessToConnector(z.id, intended).catch((err) => reportGaplessConnectorFailure(z.id, intended, err));
  }
  return zones;
}

export function resetGaplessReconcileForTests(): void {
  lastAttemptAt.clear();
}
